/**
 * CalendarSettingsMenu — the header "⋮" menu where the user configures
 * display settings that persist as component settings (surface uiState):
 *   - Time format: 12-hour (AM/PM) vs 24-hour.
 *   - Up to two additional timezone columns for the full-view gutter.
 *
 * These override whatever the agent set via props; the choice sticks across
 * surface updates (see AuiCalendar's uiState resolution).
 */

import { MoreVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import type { TimezoneSpec } from './calendar.types';

const MAX_TZ = 2;

/** Curated common zones offered in the menu. The user's own/agent's zones are
 *  merged in below so they always remain toggleable. */
const COMMON_TIMEZONES: TimezoneSpec[] = [
  { tz: 'America/Los_Angeles', label: 'San Francisco' },
  { tz: 'America/New_York', label: 'New York' },
  { tz: 'America/Chicago', label: 'Austin' },
  { tz: 'America/Sao_Paulo', label: 'São Paulo' },
  { tz: 'Europe/London', label: 'London' },
  { tz: 'Europe/Zurich', label: 'Bern' },
  { tz: 'Europe/Paris', label: 'Paris' },
  { tz: 'Europe/Istanbul', label: 'Istanbul' },
  { tz: 'Asia/Dubai', label: 'Dubai' },
  { tz: 'Asia/Kolkata', label: 'India' },
  { tz: 'Asia/Singapore', label: 'Singapore' },
  { tz: 'Asia/Shanghai', label: 'Beijing' },
  { tz: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { tz: 'Asia/Tokyo', label: 'Tokyo' },
  { tz: 'Australia/Sydney', label: 'Sydney' },
  { tz: 'UTC', label: 'UTC' },
];

export interface CalendarSettingsMenuProps {
  /** Effective time format (resolved): true = 12h, false = 24h. */
  hour12: boolean;
  /** Effective active extra timezone columns. */
  timezones: TimezoneSpec[];
  onHour12Change: (v: boolean) => void;
  onTimezonesChange: (tzs: TimezoneSpec[]) => void;
}

export function CalendarSettingsMenu({
  hour12,
  timezones,
  onHour12Change,
  onTimezonesChange,
}: CalendarSettingsMenuProps) {
  const active = new Set(timezones.map((z) => z.tz));
  const atMax = timezones.length >= MAX_TZ;

  // Merge any active zone not in the curated list so it stays removable.
  const options = [...COMMON_TIMEZONES];
  for (const z of timezones) {
    if (!options.some((o) => o.tz === z.tz)) options.push(z);
  }

  const toggleTz = (z: TimezoneSpec, checked: boolean) => {
    if (checked) {
      if (atMax) return;
      onTimezonesChange([...timezones, z]);
    } else {
      onTimezonesChange(timezones.filter((t) => t.tz !== z.tz));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Calendar settings"
          className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-ring"
        >
          <MoreVertical className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Time format</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={hour12 ? '12h' : '24h'}
          onValueChange={(v) => onHour12Change(v === '12h')}
        >
          <DropdownMenuRadioItem value="12h" onSelect={(e) => e.preventDefault()}>
            12-hour (AM/PM)
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="24h" onSelect={(e) => e.preventDefault()}>
            24-hour
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        <DropdownMenuLabel>
          Additional timezones ({timezones.length}/{MAX_TZ})
        </DropdownMenuLabel>
        <div className="max-h-60 overflow-y-auto">
          {options.map((z) => {
            const checked = active.has(z.tz);
            return (
              <DropdownMenuCheckboxItem
                key={z.tz}
                checked={checked}
                disabled={!checked && atMax}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(c) => toggleTz(z, c)}
              >
                {z.label ?? z.tz}
              </DropdownMenuCheckboxItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
